import {
  useMutation,
  useQueryClient,
  useSuspenseQuery,
} from "@tanstack/react-query"
import { createFileRoute, redirect } from "@tanstack/react-router"
import { CheckoutFailReason } from "ipctg-inflow-types/InFlowEnums"

import api from "@/utilities/api"

import Cart from "@/globalComponents/organisms/Cart"
import { ExpectedError } from "@/utilities/expectedErrors"
import {
  applyReservePromoMutationOptions,
  reserveCheckoutQueryOptions,
} from "@/utilities/hooks/queries/reserve"
import { log } from "@/utilities/logging"

type TimeoutReason = "holdExpired" | "windowEnded"

const TIMEOUT_REASON_MAP = {
  [CheckoutFailReason.ReservationHoldExpired]: "holdExpired",
  [CheckoutFailReason.ReservationWindowEnded]: "windowEnded",
} as const

function isTimeoutError(error: unknown): error is ExpectedError {
  if (!(error instanceof ExpectedError)) return false
  return error.failReason in TIMEOUT_REASON_MAP
}

function timeoutReason(error: ExpectedError): TimeoutReason {
  return (
    TIMEOUT_REASON_MAP[error.failReason as keyof typeof TIMEOUT_REASON_MAP] ||
    "holdExpired"
  )
}

export const Route = createFileRoute(
  "/qr/$qrLookupID/reserve/$epoch/calendars/$calendarID/dates/$year/$month/$day/cart/"
)({
  staticData: {
    pageName: "Cart",
  },
  loader: async ({ context, params }) => {
    try {
      await context.queryClient.ensureQueryData(reserveCheckoutQueryOptions())
    } catch (error) {
      if (isTimeoutError(error)) {
        throw redirect({
          to: "/qr/$qrLookupID/reserve/$epoch/calendars/$calendarID/dates/$year/$month/$day/cart/expired",
          params,
          search: { reason: timeoutReason(error) },
        })
      }
      throw error
    }
  },
  component: RouteComponent,
})

function RouteComponent() {
  const navigate = Route.useNavigate()
  const queryClient = useQueryClient()
  const {
    data: checkoutData,
    refetch,
    isRefetching,
  } = useSuspenseQuery(reserveCheckoutQueryOptions())

  const applyPromotionMutation = useMutation(
    applyReservePromoMutationOptions(queryClient)
  )

  const onCheckoutMutation = useMutation({
    mutationFn: onCheckoutMutationFn,
    throwOnError: (error) => !isTimeoutError(error),
    onError: (error) => {
      if (isTimeoutError(error)) {
        navigate({
          to: "/qr/$qrLookupID/reserve/$epoch/calendars/$calendarID/dates/$year/$month/$day/cart/expired",
          search: { reason: timeoutReason(error) },
        })
      }
    },
  })

  return (
    <Cart
      productType="reserve"
      transaction={checkoutData.transactionDetails}
      cancellationDeadline={checkoutData.cancellationDeadline}
      mutations={{
        applyPromotion: applyPromotionMutation,
        resetPromotion: {
          mutate: () => refetch(),
          isPending: isRefetching,
        },
        onGoNext: onCheckoutMutation,
      }}
    />
  )

  async function onCheckoutMutationFn(promoCode: string) {
    if (promoCode) {
      try {
        const cognitoIDToken = await api.auth.getCognitoIDToken()
        await api.reserve.promoPayment.POST({
          cognitoIDToken: cognitoIDToken || "",
          promoCode,
        })
      } catch (e) {
        log({
          level: "warn",
          flow: "reserve",
          scope: "fetchResponse.promoPayment",
          message:
            "Failed to apply promo code at checkout, continuing without it",
          meta: { error: e },
        })
      }
    }

    await navigate({
      to: "/qr/$qrLookupID/reserve/$epoch/calendars/$calendarID/dates/$year/$month/$day/checkout",
    })
  }
}
